import { type FC, Suspense } from "react";
import type { MetaAdsAdReportItem } from "shared";
import { useMetaAdsAds } from "../useMetaAdsAds";
import { useMetaAdsStatusUpdate } from "../useMetaAdsStatusUpdate";
import { MetricsColumns } from "./MetricsColumns";
import { StatusToggle } from "./StatusToggle";

const AdTableInner: FC<{
	adSetId: string;
	from: string;
	to: string;
	showStatusToggle: boolean;
}> = ({ adSetId, from, to, showStatusToggle }) => {
	const { data: ads } = useMetaAdsAds(adSetId, from, to);
	const { updateAdStatus } = useMetaAdsStatusUpdate();

	if (ads.length === 0) {
		return (
			<tr>
				<td colSpan={12} style={{ textAlign: "center" }} className="text-muted">
					No ads found
				</td>
			</tr>
		);
	}

	return (
		<>
			{ads.map((ad: MetaAdsAdReportItem) => (
				<tr
					key={ad.objectId}
					style={{
						background:
							ad.effectiveStatus === "DISAPPROVED"
								? "var(--bs-danger-bg-subtle, #f8d7da)"
								: undefined,
					}}
				>
					<td style={{ paddingLeft: 64 }}>
						{ad.name}
						{ad.effectiveStatus === "DISAPPROVED" && (
							<span
								className="badge bg-danger"
								style={{ marginLeft: 8 }}
								title={ad.rejectionReason ?? "Disapproved"}
							>
								Disapproved
							</span>
						)}
					</td>
					<td>
						<StatusBadge status={ad.effectiveStatus} />
						{showStatusToggle &&
							(ad.effectiveStatus === "ACTIVE" ||
								ad.effectiveStatus === "PAUSED") && (
								<span style={{ marginLeft: 8 }}>
									<StatusToggle
										objectId={ad.objectId}
										currentStatus={ad.effectiveStatus}
										onToggle={(id, status) =>
											updateAdStatus.mutate({
												adId: id,
												status,
											})
										}
										isPending={updateAdStatus.isPending}
									/>
								</span>
							)}
					</td>
					{showStatusToggle && <td />}
					<MetricsColumns item={ad} />
				</tr>
			))}
		</>
	);
};

const StatusBadge: FC<{ status: string }> = ({ status }) => {
	const colorMap: Record<string, string> = {
		ACTIVE: "bg-success",
		PAUSED: "bg-warning",
		DISAPPROVED: "bg-danger",
	};
	return (
		<span className={`badge ${colorMap[status] ?? "bg-secondary"}`}>
			{status}
		</span>
	);
};

export const AdTable: FC<{
	adSetId: string;
	from: string;
	to: string;
	showStatusToggle?: boolean;
}> = ({ showStatusToggle = false, ...props }) => (
	<Suspense
		fallback={
			<tr>
				<td colSpan={12} style={{ textAlign: "center" }}>
					Loading ads...
				</td>
			</tr>
		}
	>
		<AdTableInner {...props} showStatusToggle={showStatusToggle} />
	</Suspense>
);
